import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import type React from 'react'
import { useRef, useCallback } from 'react'
import {
  isScrollingAtom,
  storedIndicatorPosAtom,
  storedSpeedAtom,
} from '../../store/output.store'
import type { Cue } from '@rundown-studio/types'

export const MIN_SPEED = -20
export const MAX_SPEED = 20

interface PrompterScrollReturn {
  containerRef: React.RefObject<HTMLDivElement>
  startScrolling: () => void
  stopScrolling: () => void
  toggleScrolling: () => void
  resetScroll: () => void
  currentSpeed: number
  setCurrentSpeed: (speed: number) => void
  jumpToActiveCue: () => void
  scrollToCue: (cueId: Cue['id']) => void
}

const usePrompterScroll = (): PrompterScrollReturn => {
  const scrollIntervalRef = useRef<globalThis.NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isScrollingRef = useRef<boolean>(false)
  const scrollSpeedRef = useRef<number>(2)
  const [currentSpeed, setInternalSpeed] = useAtom(storedSpeedAtom)
  const setIsScrolling = useSetAtom(isScrollingAtom)
  const indicatorPosition = useAtomValue(storedIndicatorPosAtom)

  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
    isScrollingRef.current = false
    setIsScrolling(false)
  }, [setIsScrolling])

  const startScrollingWithSpeed = useCallback(
    (speed: number) => {
      if (isScrollingRef.current) return

      isScrollingRef.current = true
      setIsScrolling(true)

      // Calculate scroll increment based on user's speed setting
      // We multiply by 0.6 to convert the user-facing speed values (whole numbers like 1, 2, 3...)
      // into smoother, more natural scrolling speeds (0.6, 1.2, 1.8...)
      let scrollIncrement = speed * 0.6
      // Safari requires a minimum scroll value of 1 to properly animate scrolling
      // At speed=1, we would get 0.6, which Safari ignores, so we clamp to 1
      if (scrollIncrement === 0.6) scrollIncrement = 1

      // Calculate interval timing using absolute speed value
      const intervalTiming = Math.max(20, 50 - Math.abs(speed) * 3)

      scrollIntervalRef.current = setInterval(() => {
        if (containerRef.current) {
          const container = containerRef.current
          const newScrollTop = container.scrollTop + scrollIncrement

          // Check boundaries based on scroll direction
          if (speed > 0) {
            // Forward scrolling - check if we've reached the bottom
            if (
              newScrollTop >=
              container.scrollHeight - container.clientHeight
            ) {
              stopScrolling()
              return
            }
          } else if (speed < 0) {
            // Reverse scrolling - check if we've reached the top
            if (newScrollTop <= 0) {
              container.scrollTop = 0 // Ensure we don't go below 0
              stopScrolling()
              return
            }
          }
          container.scrollTop = newScrollTop
        }
      }, intervalTiming)
    },
    [setIsScrolling, stopScrolling],
  )

  const startScrolling = useCallback(() => {
    startScrollingWithSpeed(scrollSpeedRef.current)
  }, [startScrollingWithSpeed])

  const setCurrentSpeed = useCallback(
    (speed: number) => {
      // Clamp the speed to valid range
      const clampedSpeed = Math.max(MIN_SPEED, Math.min(MAX_SPEED, speed))

      // Update the stored speed
      setInternalSpeed(clampedSpeed)
      scrollSpeedRef.current = clampedSpeed

      // If currently scrolling, restart with the new speed
      if (isScrollingRef.current) {
        stopScrolling()
        // Use the new speed directly instead of waiting for state update
        startScrollingWithSpeed(clampedSpeed)
      }
    },
    [setInternalSpeed, stopScrolling, startScrollingWithSpeed],
  )

  const toggleScrolling = useCallback(() => {
    if (isScrollingRef.current) {
      stopScrolling()
    } else {
      startScrolling()
    }
  }, [startScrolling, stopScrolling])

  const resetScroll = useCallback(() => {
    stopScrolling()
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [stopScrolling])

  const jumpToActiveCue = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current
      const activeCell = document.getElementById('active-cell')

      // No active cell found
      if (!activeCell) return

      // Calculate the target scroll position
      const cellTop = activeCell.offsetTop
      const containerHeight = container.clientHeight
      const indicatorOffset = (indicatorPosition / 100) * containerHeight

      // Scroll so the active cell appears at the indicator position
      const newScrollTop = cellTop - indicatorOffset

      container.scrollTop = newScrollTop
    }
  }, [indicatorPosition])

  const scrollToCue = useCallback(
    (cueId: string) => {
      if (containerRef.current) {
        const container = containerRef.current
        const cueToScrollTo = document.querySelector(
          `[data-cue-id="${cueId}"]`,
        ) as HTMLElement

        // Cannot find cueId found
        if (!cueToScrollTo) return

        // Calculate the target scroll position
        const cellTop = cueToScrollTo.offsetTop
        const containerHeight = container.clientHeight
        const indicatorOffset = (indicatorPosition / 100) * containerHeight

        // Scroll so the active cell appears at the indicator position
        const newScrollTop = cellTop - indicatorOffset

        container.scrollTop = newScrollTop
      }
    },
    [indicatorPosition],
  )

  return {
    containerRef,
    startScrolling,
    stopScrolling,
    toggleScrolling,
    resetScroll,
    currentSpeed,
    setCurrentSpeed,
    jumpToActiveCue,
    scrollToCue,
  }
}

export default usePrompterScroll
